import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styles from './EncounterInformationForm.module.scss';
import Dropdown from '../../../../components/dropdown/Dropdown';
import CustomDatePicker from '../../../../components/customDatePicker/CustomDatePicker';
import TimePicker from '../../../../components/timePicker/TimePicker';
import TextField from '../../../../components/textField/TextField';
import TextAreaField from '../../../../components/textAreaField/TextAreaField';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { compareTimes } from '../../../../utils/timeUtils';
import axiosClient from '../../../../api/axiosClient';


const EncounterInformationForm = forwardRef(({
  formData,
  clinicalSitesList = [],
  instructorList = [],
  patientAgeList = [],
  ageRangesList = [],
  patientGenderList = [],
  patientRaceList = [],
  settingList = [],
  encounterTypeList = [],
  typeOfVisitList = [],
  studentParticipationList = [],
  decisionMakingList = [],
  onSave,
  disabled = false,
}, ref) => {
  const [clinicalSiteId, setClinicalSiteId] = useState(null);
  const [clinicalSiteIdError, setClinicalSiteIdError] = useState('');
  const [dateOfCase, setDateOfCase] = useState(null);
  const [dateOfCaseError, setDateOfCaseError] = useState('');
  const [instructorId, setInstructorId] = useState(null);
  const [instructorIdError, setInstructorIdError] = useState('');
  const [caseCount, setCaseCount] = useState(null);
  const [startTime, setStartTime] = useState('');
  const [startTimeError, setStartTimeError] = useState('');
  const [endTime, setEndTime] = useState('');
  const [endTimeError, setEndTimeError] = useState('');
  const [patientAge, setPatientAge] = useState(null);
  const [patientAgeError, setPatientAgeError] = useState('');
  const [patientAgeRange, setPatientAgeRange] = useState(null);
  const [patientAgeRangeError, setPatientAgeRangeError] = useState('');
  const [patientGender, setPatientGender] = useState(null);
  const [patientRace, setPatientRace] = useState(null);
  const [setting, setSetting] = useState(null);
  const [encounterType, setEncounterType] = useState(null);
  const [typeOfVisit, setTypeOfVisit] = useState(null);
  const [studentParticipation, setStudentParticipation] = useState(null);
  const [decisionMaking, setDecisionMaking] = useState(null);
  const [timeSpentWithPatient, setTimeSpentWithPatient] = useState('');
  const [timeSpentConsultingWithPreceptor, setTimeSpentConsultingWithPreceptor] = useState('');
  const [postEncounterDiagnoses, setPostEncounterDiagnoses] = useState('');
  const [showIcd10List, setShowIcd10List] = useState(false);
  const [icd10List, setIcd10List] = useState([]);
  const debounceRef = useRef(null);
  const [reasonForVisit, setReasonForVisit] = useState('');
  const [medications, setMedications] = useState('');

  useEffect(() => {
    setClinicalSiteId(formData?.clinicalSiteId ?? null);
    setDateOfCase(formData?.dateOfCase ? new Date(formData.dateOfCase) : null);
    setCaseCount(formData?.caseCount ?? null);
    setInstructorId(formData?.instructorId ?? null);
    setStartTime(formData?.startTime ?? '');
    setEndTime(formData?.endTime ?? '');
    setPatientAge(formData?.patientAge ?? null);
    setPatientAgeRange(formData?.patientAgeRange ?? null);
    setPatientGender(formData?.patientGender ?? null);
    setPatientRace(formData?.patientRace ?? null);
    setSetting(formData?.setting ?? null);
    setEncounterType(formData?.encounterType ?? null);
    setTypeOfVisit(formData?.typeOfVisit ?? null);
    setStudentParticipation(formData?.studentParticipation ?? null);
    setDecisionMaking(formData?.decisionMaking ?? null);
    setTimeSpentWithPatient(formData?.timeSpentWithPatient ?? '');
    setTimeSpentConsultingWithPreceptor(formData?.timeSpentConsultingWithPreceptor ?? '');
    const rawCode = formData?.postEncounterDiagnoses ?? '';
    if (rawCode) {
      axiosClient.get('/icd10/list', { params: { search: rawCode, limit: 10, page: 1 } })
        .then((response) => {
          const items = response.data?.data ?? [];
          const match = items.find((item) => item.code === rawCode);
          setPostEncounterDiagnoses(match ? `${match.code} - ${match.shortDescription}` : rawCode);
        })
        .catch(() => setPostEncounterDiagnoses(rawCode));
    } else {
      setPostEncounterDiagnoses('');
    }
    setReasonForVisit(formData?.reasonForVisit ?? '');
    setMedications(formData?.medications ?? '');
  }, [formData]);

  const handleIcd10Search = (value) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!value) { setIcd10List([]); return; }
      try {
        const response = await axiosClient.get('/icd10/list', { params: { search: value, limit: 10, page: 1 } });
        const items = response.data?.data ?? [];
        setIcd10List(items.map((item) => `${item.code} - ${item.shortDescription}`));
      } catch {
        setIcd10List([]);
      }
    }, 300);
  };

  const validateClinicalSiteId = () => {
    if (!clinicalSiteId) return 'Clinical Site is required';
    return '';
  };

  const validateDateOfCase = () => {
    if (!dateOfCase) return 'Date of Case is required';
    return '';
  };

  const validateInstructorId = () => {
    if (!instructorId) return 'Instructor is required';
    return '';
  };

  const validateStartTime = () => {
    if (!startTime) return 'Start Time is required';
    if (endTime && !compareTimes(startTime, endTime)) return 'Start time cannot be later than or equal to end time';
    return '';
  };

  const validateEndTime = () => {
    if (!endTime) return 'End Time is required';
    if (startTime && !compareTimes(startTime, endTime)) return 'End time cannot be earlier than or equal to start time';
    return '';
  };

  const validatePatientAge = () => {
    if (!patientAge) return 'Patient\'s Age is required';
    return '';
  };

  const validatePatientAgeRange = () => {
    if (!patientAgeRange) return 'Age Range is required';
    return '';
  };

  const [settingError, setSettingError] = useState('');
  const [encounterTypeError, setEncounterTypeError] = useState('');
  const [typeOfVisitError, setTypeOfVisitError] = useState('');
  const [studentParticipationError, setStudentParticipationError] = useState('');
  const [decisionMakingError, setDecisionMakingError] = useState('');
  const [postEncounterDiagnosesError, setPostEncounterDiagnosesError] = useState('');

  const validateSetting = () => {
    if (!setting) return 'Setting is required';
    return '';
  };

  const validateEncounterType = () => {
    if (!encounterType) return 'Encounter Type is required';
    return '';
  };

  const validateTypeOfVisit = () => {
    if (!typeOfVisit) return 'Type of Visit is required';
    return '';
  };

  const validateStudentParticipation = () => {
    if (!studentParticipation) return 'Student Participation is required';
    return '';
  };

  const validateDecisionMaking = () => {
    if (!decisionMaking) return 'Decision Making is required';
    return '';
  };

  const validatePostEncounterDiagnoses = () => {
    if (!postEncounterDiagnoses) return 'Post-encounter diagnosis(es) is required';
    return '';
  };

  const validateForm = () => {
    const clinicalSiteIdResult = validateClinicalSiteId();
    const dateOfCaseResult = validateDateOfCase();
    const instructorIdResult = validateInstructorId();
    const startTimeResult = validateStartTime();
    const endTimeResult = validateEndTime();
    const patientAgeResult = validatePatientAge();
    const patientAgeRangeResult = validatePatientAgeRange();
    const settingResult = validateSetting();
    const encounterTypeResult = validateEncounterType();
    const typeOfVisitResult = validateTypeOfVisit();
    const studentParticipationResult = validateStudentParticipation();
    const decisionMakingResult = validateDecisionMaking();
    const postEncounterDiagnosesResult = validatePostEncounterDiagnoses();

    setClinicalSiteIdError(clinicalSiteIdResult);
    setDateOfCaseError(dateOfCaseResult);
    setInstructorIdError(instructorIdResult);
    setStartTimeError(startTimeResult);
    setEndTimeError(endTimeResult);
    setPatientAgeError(patientAgeResult);
    setPatientAgeRangeError(patientAgeRangeResult);
    setSettingError(settingResult);
    setEncounterTypeError(encounterTypeResult);
    setTypeOfVisitError(typeOfVisitResult);
    setStudentParticipationError(studentParticipationResult);
    setDecisionMakingError(decisionMakingResult);
    setPostEncounterDiagnosesError(postEncounterDiagnosesResult);

    return [clinicalSiteIdResult, dateOfCaseResult, instructorIdResult, startTimeResult, endTimeResult, patientAgeResult, patientAgeRangeResult, settingResult, encounterTypeResult, typeOfVisitResult, studentParticipationResult, decisionMakingResult, postEncounterDiagnosesResult]
      .filter(e => e !== '').length;
  };

  useImperativeHandle(ref, () => ({ validateForm }));

  return (
    <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
      <div className={styles.row}>
        <div className={styles.column50}>
          <Dropdown
            label="Clinical Site"
            options={clinicalSitesList}
            value={clinicalSiteId}
            onChange={(value) => {
              setClinicalSiteId(value);
              setClinicalSiteIdError('');
              onSave({ clinicalSiteId: value });
            }}
            error={clinicalSiteIdError}
            searchable
            disabled={disabled}
          />
        </div>
        <div className={styles.column20}>
          <CustomDatePicker
            label="Date of Case"
            date={dateOfCase}
            onChange={(value) => {
              setDateOfCase(value);
              setDateOfCaseError('');
              onSave({ dateOfCase: value ? format(value, 'yyyy-MM-dd') : null });
            }}
            showIcon={true}
            maxDate={new Date()}
            error={dateOfCaseError}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column40}>
          <Dropdown
            label="Choose your Instructor"
            options={instructorList}
            value={instructorId}
            onChange={(value) => {
              setInstructorId(value);
              setInstructorIdError('');
              onSave({ instructorId: value });
            }}
            error={instructorIdError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column20}>
          <TextField
            label="Case Count"
            type="number"
            value={caseCount}
            onChange={(value) => setCaseCount(value)}
            disabled={true}
          />
        </div>
        <div className={styles.column20}>
          <TimePicker
            label="Start Time"
            value={startTime}
            onChange={(value) => {
              setStartTime(value);
              onSave({ startTime: value });
            }}
            error={startTimeError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column20}>
          <TimePicker
            label="End Time"
            value={endTime}
            onChange={(value) => {
              setEndTime(value);
              onSave({ endTime: value });
            }}
            error={endTimeError}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column20}>
          <Dropdown
            label="Patient's Age"
            options={patientAgeList}
            value={patientAge}
            onChange={(value) => {
              setPatientAge(value);
              setPatientAgeError('');
              onSave(typeof value === 'string' ? {
                patientAge: value,
                patientAgeMonth: ''
              } : {
                patientAge: value.year,
                patientAgeMonth: value.month
              });
            }}
            searchable={true}
            error={patientAgeError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column20}>
          <Dropdown
            label="Age Range"
            options={ageRangesList}
            value={patientAgeRange}
            onChange={(value) => {
              setPatientAgeRange(value);
              setPatientAgeRangeError('');
              onSave({ patientAgeRange: value });
            }}
            error={patientAgeRangeError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column20}>
          <Dropdown
            label="Patient's Gender"
            options={patientGenderList}
            value={patientGender}
            onChange={(value) => {
              setPatientGender(value);
              onSave({ patientGender: value });
            }}
            disabled={disabled}
          />
        </div>
        <div className={styles.column40}>
          <Dropdown
            label="Patient's Race"
            options={patientRaceList}
            value={patientRace}
            onChange={(value) => {
              setPatientRace(value);
              onSave({ patientRace: value });
            }}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column33}>
          <Dropdown
            label="Setting"
            options={settingList}
            value={setting}
            onChange={(value) => {
              setSetting(value);
              setSettingError('');
              onSave({ setting: value });
            }}
            error={settingError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column33}>
          <Dropdown
            label="Type of Visit"
            options={typeOfVisitList}
            value={typeOfVisit}
            onChange={(value) => {
              setTypeOfVisit(value);
              setTypeOfVisitError('');
              onSave({ typeOfVisit: value });
            }}
            error={typeOfVisitError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column33}>
          <Dropdown
            label="Encounter Type"
            options={encounterTypeList}
            value={encounterType}
            onChange={(value) => {
              setEncounterType(value);
              setEncounterTypeError('');
              onSave({ encounterType: value });
            }}
            error={encounterTypeError}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column50}>
          <Dropdown
            label="Student Participation"
            options={studentParticipationList}
            value={studentParticipation}
            onChange={(value) => {
              setStudentParticipation(value);
              setStudentParticipationError('');
              onSave({ studentParticipation: value });
            }}
            error={studentParticipationError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column30}>
          <Dropdown
            label="Decision Making"
            options={decisionMakingList}
            value={decisionMaking}
            onChange={(value) => {
              setDecisionMaking(value);
              setDecisionMakingError('');
              onSave({ decisionMaking: value });
            }}
            error={decisionMakingError}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column20}>
          <TimePicker
            label="Time Spent With Patient"
            value={timeSpentWithPatient}
            onChange={(value) => {
              setTimeSpentWithPatient(value);
              onSave({ timeSpentWithPatient: value });
            }}
            disabled={disabled}
          />
        </div>
        <div className={styles.column30}>
          <TimePicker
            label="Time Spent Consulting With Preceptor"
            value={timeSpentConsultingWithPreceptor}
            onChange={(value) => {
              setTimeSpentConsultingWithPreceptor(value);
              onSave({ timeSpentConsultingWithPreceptor: value });
            }}
            disabled={disabled}
          />
        </div>
        <div
          className={styles.icd10Wrapper}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setShowIcd10List(false);
              onSave({ postEncounterDiagnoses: postEncounterDiagnoses.split(' - ')[0] });
            }
          }}
        >
          <TextField
            label="Post-encounter diagnosis(es)"
            type="text"
            value={postEncounterDiagnoses}
            onChange={(value) => {
              setPostEncounterDiagnoses(value);
              setPostEncounterDiagnosesError('');
              setShowIcd10List(true);
              handleIcd10Search(value);
            }}
            error={postEncounterDiagnosesError}
            disabled={disabled}
          />
          {showIcd10List && icd10List.length > 0 && (
            <ul className={styles.icd10List} onMouseDown={(e) => e.preventDefault()}>
              {icd10List.map((item) => (
                <li
                  key={item}
                  onClick={() => {
                    setPostEncounterDiagnoses(item);
                    setShowIcd10List(false);
                    setPostEncounterDiagnosesError('');
                    onSave({ postEncounterDiagnoses: item.split(' - ')[0] });
                  }}
                >
                  {item}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.columnFull}>
          <TextAreaField
            label="Reason for Visit"
            value={reasonForVisit}
            onChange={(value) => setReasonForVisit(value)}
            onBlur={() => onSave({ reasonForVisit })}
            limit={100}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.columnFull}>
          <TextAreaField
            label="Medications"
            value={medications}
            onChange={(value) => setMedications(value)}
            onBlur={() => onSave({ medications })}
            limit={500}
            disabled={disabled}
          />
        </div>
      </div>
    </form>
  );
});

EncounterInformationForm.displayName = 'EncounterInformationForm';

EncounterInformationForm.propTypes = {
  formData: PropTypes.shape({
    clinicalSiteId: PropTypes.number,
    dateOfCase: PropTypes.any,
    caseCount: PropTypes.number,
    instructorId: PropTypes.number,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    patientAge: PropTypes.any,
    patientAgeRange: PropTypes.string,
    patientGender: PropTypes.string,
    patientRace: PropTypes.string,
    setting: PropTypes.string,
    encounterType: PropTypes.string,
    typeOfVisit: PropTypes.string,
    studentParticipation: PropTypes.string,
    decisionMaking: PropTypes.string,
    timeSpentWithPatient: PropTypes.string,
    timeSpentConsultingWithPreceptor: PropTypes.string,
    postEncounterDiagnoses: PropTypes.string,
    reasonForVisit: PropTypes.string,
    medications: PropTypes.string,
  }),
  clinicalSitesList: PropTypes.array,
  instructorList: PropTypes.array,
  patientAgeList: PropTypes.array,
  ageRangesList: PropTypes.array,
  patientGenderList: PropTypes.array,
  patientRaceList: PropTypes.array,
  settingList: PropTypes.array,
  encounterTypeList: PropTypes.array,
  typeOfVisitList: PropTypes.array,
  studentParticipationList: PropTypes.array,
  decisionMakingList: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default EncounterInformationForm;
